/**
 * Plan Context - Plan State Management
 *
 * Implements:
 * - REQ-006: WebSocket Connection Management
 * - REQ-007: Offline Sync Protocol
 * - REQ-015: Pending Queue (Web)
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useIdentity } from "./IdentityContext";
import { addRecentPlan, extractPlanName } from "../lib/storage";

interface LogEntry {
  seq: number;
  ts: string;
  agent: string;
  forms: string[];
  sig: string;
}

interface PendingAppend {
  payload: { type: "append"; forms: string[] };
  sig: string;
  pubkey: string;
  localId: string;
}

type CapScope = "read" | "append" | "admin";

interface PlanContextValue {
  token: string | null;
  entries: LogEntry[];
  pendingCount: number;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  scope: CapScope;
  canAppend: boolean;
  append: (forms: string[]) => Promise<void>;
}

/**
 * Decode scope from capability token (byte 16)
 * Token format: base64url(plan_id[16] || scope[1] || expiry[8] || hmac[32])
 */
function decodeScope(token: string): CapScope {
  try {
    // Base64url decode
    const pad = token.length % 4;
    const padded = pad ? token + "=".repeat(4 - pad) : token;
    const base64 = padded.replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(base64);

    if (binary.length < 17) return "read";

    const scopeByte = binary.charCodeAt(16);
    if (scopeByte === 2) return "admin";
    if (scopeByte === 1) return "append";
    return "read";
  } catch {
    return "read";
  }
}

const PlanContext = createContext<PlanContextValue | null>(null);

export function PlanProvider({ children }: { children: ReactNode }) {
  const { token } = useParams<{ token: string }>();
  const { publicKey, sign, isLoading: identityLoading } = useIdentity();

  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [pending, setPending] = useState<PendingAppend[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const [reconnectCount, setReconnectCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [httpDone, setHttpDone] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);

  // Track page visibility to disconnect WebSocket when backgrounded
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Fetch initial log via HTTP (fast, reliable)
  useEffect(() => {
    // Skip for demo and test tokens (local files)
    if (!token || token === "demo" || token.startsWith("test-")) {
      setHttpDone(true);
      return;
    }

    fetch(`/p/${token}/log`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: { entries: LogEntry[]; total: number }) => {
        setEntries(data.entries);
        setIsLoading(false);
        setHttpDone(true);
      })
      .catch((err) => {
        console.error("Failed to fetch log:", err);
        setHttpDone(true);
        // Don't set error here - WebSocket may still work
      });
  }, [token]);

  // Connect WebSocket for live updates (skip for demo mode)
  useEffect(() => {
    if (!token || identityLoading) return;

    // Demo/test mode - skip WebSocket connection
    if (token === "demo" || token.startsWith("test-")) {
      setIsLoading(false);
      setIsConnected(false);
      return;
    }

    // Don't connect when page is backgrounded (saves resources)
    if (!isPageVisible) {
      // Close existing connection when page becomes hidden
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, "Page hidden");
        wsRef.current = null;
      }
      return;
    }

    // Already connected or connecting
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/p/${token}`;

    const socket = new WebSocket(wsUrl);
    let pingInterval: ReturnType<typeof setInterval> | null = null;
    let shouldClose = false; // Flag for cleanup during CONNECTING state

    socket.onopen = () => {
      // If cleanup was called while connecting, close immediately
      if (shouldClose) {
        socket.close(1000, "Component unmounted");
        return;
      }

      setIsConnected(true);
      setError(null);

      // Send sync request with current lastSeq
      const lastSeq = entries.length > 0 ? entries[entries.length - 1].seq : 0;
      socket.send(
        JSON.stringify({
          type: "sync",
          lastSeq,
          pending: pending,
        })
      );

      // Keep-alive ping every 30 seconds to prevent idle disconnect
      pingInterval = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: "ping" }));
        }
      }, 30000);
    };

    socket.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        if (msg.type === "sync_ack") {
          // Process confirmed pending items
          const confirmedIds = new Set(msg.confirmed.map((c: { localId: string }) => c.localId));
          setPending((prev) => prev.filter((p) => !confirmedIds.has(p.localId)));

          // Merge missed entries
          if (msg.missed.length > 0) {
            setEntries((prev) => {
              // Build set of existing seqs only if we have entries
              const existingSeqs = prev.length > 0
                ? new Set(prev.map((e) => e.seq))
                : new Set<number>();
              const newEntries = msg.missed.filter((e: LogEntry) => !existingSeqs.has(e.seq));
              if (newEntries.length === 0) return prev;
              // If all new entries are after the last existing, just append
              const lastSeq = prev.length > 0 ? prev[prev.length - 1].seq : 0;
              const allAfter = newEntries.every((e: LogEntry) => e.seq > lastSeq);
              if (allAfter) {
                // Sort just the new entries and append
                return [...prev, ...newEntries.sort((a: LogEntry, b: LogEntry) => a.seq - b.seq)];
              }
              return [...prev, ...newEntries].sort((a, b) => a.seq - b.seq);
            });
          }

          setIsLoading(false);
        }

        if (msg.type === "entry") {
          setEntries((prev) => {
            // Fast path: check if already exists using last entry (common case)
            if (prev.length > 0 && prev[prev.length - 1].seq >= msg.entry.seq) {
              // Entry might already exist, do full check
              if (prev.some((e) => e.seq === msg.entry.seq)) return prev;
            }
            // Append to end if seq is higher than last (common case for broadcasts)
            if (prev.length === 0 || msg.entry.seq > prev[prev.length - 1].seq) {
              return [...prev, msg.entry];
            }
            // Rare case: entry needs to be inserted in middle
            return [...prev, msg.entry].sort((a, b) => a.seq - b.seq);
          });
        }

        if (msg.type === "error") {
          setError(msg.message);
        }
      } catch (err) {
        console.error("Failed to parse WebSocket message:", err);
      }
    };

    socket.onclose = (event) => {
      if (pingInterval) clearInterval(pingInterval);
      setIsConnected(false);
      wsRef.current = null;
      // Only auto-reconnect if page is visible and it wasn't a clean close
      if (event.code !== 1000 && isPageVisible) {
        setTimeout(() => setReconnectCount(c => c + 1), 3000);
      }
    };

    socket.onerror = () => {
      setIsConnected(false);
      // Error will be shown via the effect below once HTTP also completes
    };

    wsRef.current = socket;

    return () => {
      if (pingInterval) clearInterval(pingInterval);
      // If socket is still connecting, set flag to close when it opens
      // This prevents "WebSocket is closed before the connection is established" error
      if (socket.readyState === WebSocket.CONNECTING) {
        shouldClose = true;
      } else if (socket.readyState === WebSocket.OPEN) {
        socket.close(1000, "Component unmounted");
      }
      wsRef.current = null;
      // If CLOSING or CLOSED, no action needed
    };
  // reconnectCount triggers reconnection after socket closes
  // wsRef is used instead of state to avoid effect re-runs when socket is set
  }, [token, identityLoading, isPageVisible, reconnectCount]);

  // Show error only after HTTP is done and we have no data
  useEffect(() => {
    if (httpDone && !isConnected && entries.length === 0 && isLoading) {
      // Give WebSocket a moment to connect before showing error
      const timeout = setTimeout(() => {
        if (!isConnected && entries.length === 0) {
          setError("Connection lost. The worker may not be running.");
          setIsLoading(false);
        }
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [httpDone, isConnected, entries.length, isLoading]);

  const append = useCallback(
    async (forms: string[]) => {
      if (!publicKey) {
        throw new Error("No identity available");
      }

      const payload = { type: "append" as const, forms };
      const sig = await sign(payload);
      const localId = crypto.randomUUID();

      const pendingAppend: PendingAppend = {
        payload,
        sig,
        pubkey: publicKey,
        localId,
      };

      if (isConnected && wsRef.current?.readyState === WebSocket.OPEN) {
        // Send directly via sync
        const lastSeq = entries.length > 0 ? entries[entries.length - 1].seq : 0;
        wsRef.current.send(
          JSON.stringify({
            type: "sync",
            lastSeq,
            pending: [pendingAppend],
          })
        );
      } else {
        // Use HTTP fallback when WebSocket isn't available
        try {
          const res = await fetch(`/p/${token}/append`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ payload, sig, pubkey: publicKey }),
          });
          if (!res.ok) {
            const errBody = await res.json().catch(() => ({}));
            throw new Error(errBody.message || `HTTP ${res.status}`);
          }
          const result = await res.json();
          // Add the entry to local state
          setEntries((prev) => {
            if (prev.some((e) => e.seq === result.seq)) return prev;
            const newEntry: LogEntry = {
              seq: result.seq,
              ts: result.ts,
              agent: result.agent,
              forms,
              sig,
            };
            return [...prev, newEntry].sort((a, b) => a.seq - b.seq);
          });
        } catch (err) {
          console.error("Failed to append via HTTP:", err);
          // Queue for later retry
          setPending((prev) => [...prev, pendingAppend]);
        }
      }
    },
    [publicKey, sign, isConnected, entries, token]
  );

  // Decode scope from token
  const scope = token && token !== "demo" && !token.startsWith("test-")
    ? decodeScope(token)
    : "admin"; // Demo/test mode has full access
  const canAppend = scope === "append" || scope === "admin";

  // Track if we've already recorded this plan visit
  const hasRecordedVisit = useRef(false);

  // Record plan visit to recent plans list
  useEffect(() => {
    // Skip demo/test tokens
    if (!token || token === "demo" || token.startsWith("test-")) {
      return;
    }

    // Skip if loading or no entries yet
    if (isLoading || entries.length === 0) {
      return;
    }

    // Only record once per mount
    if (hasRecordedVisit.current) {
      return;
    }

    hasRecordedVisit.current = true;

    const name = extractPlanName(entries);
    addRecentPlan({
      token,
      name,
      scope,
    });
  }, [token, isLoading, entries, scope]);

  return (
    <PlanContext.Provider
      value={{
        token: token || null,
        entries,
        pendingCount: pending.length,
        isConnected,
        isLoading,
        error,
        scope,
        canAppend,
        append,
      }}
    >
      {children}
    </PlanContext.Provider>
  );
}

export function usePlan() {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error("usePlan must be used within PlanProvider");
  }
  return context;
}
